"use client";
import styles from "./ProductResults.module.scss";

import { useRef, useState } from "react";

import { EnergyType } from "@/types/data";
import { track } from "@/utilities/analytics";
import { siteAPIURL } from "@/utilities/constants";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { MaterialIcon } from "../MaterialIcon/MaterialIcon";
import { MultipleNMIAddressSelect } from "./MultipleNMIAddressSelect/MultipleNMIAddressSelect";
import { ErrorCard } from "./Product/ErrorCard/ErrorCard";
import { CheckGasAvailableCard } from "./Product/GasAvailableCard/GasAvailableCard";
import { ProductCard } from "./Product/ProductCard/ProductCard";

import { useEffect } from "react";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

import { useOnboardingStore } from "@/stores/onboardingStore";
import type { MultipleNmiAddressSelectContent, NmiSearchModalContent } from "@/sanity/queries";

interface ProductResultsProps {
	addressKey?: string;
	nmi?: string;
	promo?: string;
	energy: EnergyType;
	address?: string;
	nmiSearchModalContent?: NmiSearchModalContent | null;
	multipleNmiAddressSelectContent?: MultipleNmiAddressSelectContent | null;
}

const isPromoApplied = (planPromoCode: string | undefined, promo: string | undefined) =>
	Boolean(
		promo &&
			planPromoCode &&
			planPromoCode !== "Invalid Promo Code" &&
			planPromoCode !== "N" &&
			planPromoCode.toUpperCase() === promo.toUpperCase(),
	);

type LookupData = {
	success?: boolean;
	status?: string;
	isCommercial?: boolean;
	nmi?: string;
	plan?: { promoCode?: string; [key: string]: any };
};

/**
 * Identifies one lookup outcome, so the same result is not reported twice
 * across re-renders.
 */
const outcomeSignature = (data: LookupData) =>
	[data?.status, data?.success, data?.nmi, data?.plan?.PROD1].join("|");

/**
 * Report what the customer was actually shown.
 *
 * Driven off the resolved data rather than the fetch handler, because
 * MultipleNMIAddressSelect sets results directly and would otherwise bypass
 * this entirely.
 */
const trackLookupOutcome = (
	data: LookupData,
	energy: EnergyType,
	promo?: string,
	gasAvailable?: boolean | null,
) => {
	// Nothing has been shown yet.
	if (!data || data.status === "loading") {
		return;
	}

	// The address matched several NMIs, so there is no outcome yet — the
	// customer picks one and a fresh lookup runs.
	if (data.status === "multipleNMI") {
		return;
	}

	if (!data.success) {
		track({ event: "plans_unavailable", energy, reason: "lookup_failed" });
		return;
	}

	// PROD1 is the plan name and gates whether a plan was returned at all —
	// the same check the render uses to decide between a card and an error.
	const product: string | undefined = data?.plan?.PROD1;

	if (isEmpty(product)) {
		track({ event: "plans_unavailable", energy, reason: "no_plan" });
		return;
	}

	// A gas plan is only ever shown once the pipeline locator confirms a
	// connection at the address. Without this the customer is looking at a map,
	// not a plan.
	if (energy === "gas" && gasAvailable === false) {
		track({ event: "plans_unavailable", energy, reason: "gas_unavailable" });
		return;
	}

	track({
		event: "plan_viewed",
		energy,
		productCode: data?.plan?.PROD1_TEMPLATE ?? "",
		product: product as string,
		isCommercial: Boolean(data.isCommercial),
		promo: promo || undefined,
		promo_valid: promo ? isPromoApplied(data?.plan?.PROMOCODE, promo) : undefined,
	});
};

export const ProductResults = ({
	addressKey,
	nmi,
	promo,
	energy,
	address,
	nmiSearchModalContent,
	multipleNmiAddressSelectContent,
}: ProductResultsProps) => {
	const [data, setData] = useState<{
		isCommercial: boolean;
		signupUrl: string;
		tariffs?: string[];
		plan?: {
			promoCode?: string;
			[key: string]: any;
		};
		success?: boolean;
		status?: string;
		addresses?: { address: string; nmi: string }[]; // if multiple NMIs it will return this
		address?: { [key: string]: any };
		nmi?: string;
		promoCode?: string;
		aseXML?: string;
		installationTypeCode?: string;
	}>({
		signupUrl: "",
		tariffs: [],
		plan: {},
		success: true,
		status: "loading",
		addresses: [],
		isCommercial: false,
		nmi: "",
		promoCode: "",
	});

	const setOnboardingData = useOnboardingStore(state => state.setOnboardingData);

	// null until the pipeline locator reports back. Gas only.
	const [gasAvailable, setGasAvailable] = useState<boolean | null>(null);

	const trackedOutcome = useRef<string | null>(null);

	useEffect(() => {
		const lookupEnergy: EnergyType = nmi ? "electricity" : energy;

		const signature = `${outcomeSignature(data)}|${gasAvailable}`;

		if (trackedOutcome.current === signature) {
			return;
		}

		trackedOutcome.current = signature;

		// A gas plan exists, but the customer is shown the pipeline locator
		// instead of a card until they check their address.
		if (
			lookupEnergy === "gas" &&
			data?.success &&
			data?.status === "success" &&
			gasAvailable === null
		) {
			track({ event: "gas_availability_check", energy: "gas" });
			return;
		}

		trackLookupOutcome(data, lookupEnergy, promo, gasAvailable);
	}, [data, energy, nmi, promo, gasAvailable]);

	useEffect(() => {
		if ((addressKey || nmi) && data.status === "loading") {
			console.log(nmi ? "getPlansby-nmi" : "getPlansby-addressKey", nmi || addressKey, promo);

			const url = new URL(siteAPIURL + "/signup/get-plans");

			if (nmi) {
				url.searchParams.append("nmi", nmi);
				url.searchParams.append("energy", "electricity");
			} else if (addressKey) {
				url.searchParams.append("address", address ?? "");
				url.searchParams.append("addressKey", addressKey);
				if (energy) {
					url.searchParams.append("energy", energy);
				}
			}

			if (promo) {
				url.searchParams.append("promo", promo);
			}

			fetch(url)
				.then(response => response.json())
				.then(data => {
					setData(data);
				})
				.catch(error => {
					console.error("Failed to get plans", error);
					setData(currentData => ({
						...currentData,
						success: false,
						status: "error",
					}));
				});
		}
	}, [addressKey, nmi, promo, energy, address, data.status]);

	const isPromoValid = isPromoApplied(data?.plan?.PROMOCODE, promo);

	const validPlan = !isEmpty(data?.plan?.PROD1);

	if (data && data.status === "loading") {
		return (
			<div className={styles["loading"]}>
				<LoadingSpinner size="xl" />
				<p>Loading relevant products...</p>
			</div>
		);
	}

	if (data && data.success && data.status == "success") {
		console.log("ProductResults data:", data);
		setOnboardingData({
			energy: energy,
			isCommercial: data.isCommercial,
			plan: data.plan || {},
			nmi: data.nmi || "",
			promoCode: data.promoCode || "",
			address: data.address || {},
			aseXML: data.aseXML,
			installationTypeCode: data.installationTypeCode,
		});
	}

	return (
		<div>
			<div>
				{data.success && data.status == "success" && (
					<div className={styles["product-results"]}>
						{data?.plan?.PROMOCODE && data?.plan?.PROMOCODE !== "N" && isPromoValid && (
							<p
								className={clsx(
									styles["product-results__message"],
									styles["product-results__message--success"],
								)}
							>
								<MaterialIcon
									name="check_circle"
									className={styles["product-results__message-icon"]}
								/>
								Promo code {data?.plan?.PROMOCODE} has been applied
							</p>
						)}
						{data?.plan?.PROMOCODE && data?.plan?.PROMOCODE !== "N" && !isPromoValid && (
							<p
								className={clsx(
									styles["product-results__message"],
									styles["product-results__message--warn"],
								)}
							>
								<MaterialIcon
									name="warning"
									className={styles["product-results__message-icon"]}
								/>
								{data?.plan?.PROMOCODE}
							</p>
						)}
						{validPlan && data.plan ? (
							energy !== "gas" ? (
								<ProductCard
									energy={"electricity"}
									isCommercial={data.isCommercial}
									plan={data.plan}
									signupUrl={data.signupUrl}
									nmi={data.nmi}
									promoCode={data.promoCode}
								/>
							) : (
								<CheckGasAvailableCard
								address={address ?? ""}
								onAvailabilityChange={setGasAvailable}
							>
									<ProductCard
										energy={"gas"}
										isCommercial={data.isCommercial}
										plan={data.plan}
										signupUrl={data.signupUrl}
									/>
								</CheckGasAvailableCard>
							)
						) : (
							<ErrorCard error={{ message: "noPlan", name: "noPlan" }} />
						)}
					</div>
				)}
			</div>

			{data && data.status === "multipleNMI" && data.addresses && (
				<div className={styles["product-results"]}>
					<MultipleNMIAddressSelect
						nmis={data.addresses}
						promo={promo}
						setData={setData}
						nmiSearchModalContent={nmiSearchModalContent}
						content={multipleNmiAddressSelectContent}
					/>
				</div>
			)}

			{data && !data.success && (
				<div className={styles["product-results"]}>
					<ErrorCard error={{ message: "noPlan", name: "noPlan" }} />
				</div>
			)}
		</div>
	);
};
